import * as Yup from "yup"
import {
     API_FOURNISSEUR_SET_INFO_FOURNISSEUR_BY_FOURNISSEUR_ENDPOINT,
     API_GET_PAYS_URL,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_MIME_IMAGE_JPEG,
     CONST_MIME_IMAGE_JPG,
     CONST_MIME_IMAGE_PNG,
} from "../../../../constants"
import { Form, Formik, FormikHelpers } from "formik"
import axios, { AxiosError } from "axios"
import { toast } from "react-toastify"
import { IFournisseurModel, IHttpErrorResponseModel } from "../../../../models"
import React, { useContext, useState } from "react"
import { CompteInfoFournisseurContext } from "./CompteInfoFournisseur"
import MyAlert from "../../../../utils/MyAlert"
import MySelectGetAllDataFromServer from "../../../../utils/fields/MySelectGetAllDataFromServer"
import MyInputField from "../../../../utils/fields/MyInputField"
import MySimpleSpinner from "../../../../utils/MySimpleSpinner"
import { useAuth } from "../../../auth"
import MyLoadingOverlay from "../../../../utils/MyLoadingOverlay"
import { toAbsoluteUrl } from "../../../../../_metronic/helpers"
import FileAndDriveHelper from "../../../../helpers/FileAndDriveHelper"
import { useThemeMode } from "../../../../../_metronic/partials"
import MyInptuMaskField from "../../../../utils/fields/MyInputMaskField"

const CompteInfoFournisseurEditionMode = () => {
     const { currentUser, setCurrentUser } = useAuth()
     const { toggleToPreviewMode } = useContext(CompteInfoFournisseurContext)
     const { mode } = useThemeMode()

     const [isLogoLoading, setIsLogoLoading] = useState<boolean>(true)
     const [logoPreview, setLogoPreview] = useState<any>(
          currentUser?.fournisseur?.fournisseur?.logoGoogleDriveId
               ? FileAndDriveHelper.getThumbnailDriveUrl(currentUser?.fournisseur?.fournisseur?.logoGoogleDriveId)
               : toAbsoluteUrl(mode == "dark" ? "/media/svg/files/blank-image-dark.svg" : "/media/svg/files/blank-image.svg")
     )

     // Schema
     const schema = Yup.object().shape({
          raisonSociale: Yup.string().required().nullable().label(`"Raison sociale"`),
          siret: Yup.string().required().nullable().label(`"SIRET"`),
          adresse: Yup.string().required().nullable().label(`"Adresse"`),
          ville: Yup.string().required().nullable().label(`"ville"`),
          codePostal: Yup.string().required().nullable().label(`"Code postal"`),
          pays: Yup.number().required().nullable().label(`"Pays"`),
          logo: Yup.mixed()
               .nullable()
               .test("fileFormat", "Seuls les formats JPEG ou PNG sont acceptés", (value: any) => {
                    if (!value) return true // If no file is provided, skip the test
                    return [CONST_MIME_IMAGE_PNG, CONST_MIME_IMAGE_JPEG, CONST_MIME_IMAGE_JPG].includes(value.type)
               })
               .test("fileSize", "Le document ne doit pas dépasser le 10MB", (value: any) => {
                    if (!value) return true // If no file is provided, skip the test
                    return value.size <= 10 * 1024 * 1024
               }),
     })

     // Init values
     const initialValues: IFournisseurEditInfoByFournisseurModel = {
          raisonSociale: currentUser?.fournisseur?.fournisseur?.raisonSociale,
          siret: currentUser?.fournisseur?.fournisseur?.siret,
          adresse: currentUser?.fournisseur?.fournisseur?.adresse,
          ville: currentUser?.fournisseur?.fournisseur?.ville,
          codePostal: currentUser?.fournisseur?.fournisseur?.codePostal,
          pays: currentUser?.fournisseur?.fournisseur?.pays?.id,
     }

     // Handle submit
     function handleSubmit(values: IFournisseurEditInfoByFournisseurModel, helpers: FormikHelpers<IFournisseurEditInfoByFournisseurModel>) {
          helpers.setStatus(null)

          // Prepare payload
          const formData = new FormData()
          for (const [key, value] of Object.entries(values)) {
               if (key === "logo" && value) {
                    formData.append("logo", value)
               } else {
                    formData.append(`data[${key}]`, value)
               }
          }

          // Request
          axios.post<IFournisseurModel>(API_FOURNISSEUR_SET_INFO_FOURNISSEUR_BY_FOURNISSEUR_ENDPOINT, formData)
               .then(r => {
                    toast.success("Vos modifications ont bien été prises en compte.")
                    setCurrentUser(prev => ({ ...prev, fournisseur: { ...prev?.fournisseur, fournisseur: { ...prev?.fournisseur?.fournisseur, ...r.data } } }))
                    if (toggleToPreviewMode) toggleToPreviewMode()
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    // Set form errors
                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    // Set form global status and notify user using a toast
                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })

                    // Stop submit loader
                    helpers.setSubmitting(false)
               })
               .finally(() => helpers.setSubmitting(false))
     }

     return (
          <>
               <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {helpers => {
                         return (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   {/* Field logo */}
                                   <div className="row mb-4">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6">Logo</label>
                                        <div className="col-lg-8">
                                             <div className="image-input image-input-outline" data-kt-image-input="true" style={{ backgroundImage: `url(${logoPreview})` }}>
                                                  <MyLoadingOverlay isActive={isLogoLoading}>
                                                       <img
                                                            className="image-input-wrapper w-125px h-125px"
                                                            src={logoPreview}
                                                            onLoad={() => setIsLogoLoading(false)}
                                                            alt={"logo"}
                                                       ></img>
                                                  </MyLoadingOverlay>

                                                  <label
                                                       className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                                                       data-kt-image-input-action="change"
                                                       data-bs-original-title="Change logo"
                                                  >
                                                       <i className="bi bi-pencil-fill fs-7"></i>
                                                       <input
                                                            type="file"
                                                            name="logo"
                                                            accept=".png, .jpg, .jpeg"
                                                            onChange={e => {
                                                                 const file = e.target.files![0]
                                                                 helpers.setFieldValue("logo", file)
                                                                 const reader = new FileReader()
                                                                 reader.onloadend = () => {
                                                                      // Set the image preview URL
                                                                      setLogoPreview(reader.result as string)
                                                                 }
                                                                 reader.readAsDataURL(file)
                                                            }}
                                                       />
                                                  </label>
                                             </div>
                                        </div>
                                   </div>
                                   {helpers.errors.logo && <div className={"text-danger"}>{helpers.errors.logo}</div>}

                                   {/* Nom commercial */}
                                   <div className="row mb-5">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Nom commercial</label>

                                        <div className="col-lg-8">
                                             <MyInputField
                                                  name={"raisonSociale"}
                                                  value={helpers.values.raisonSociale}
                                                  onChange={val => helpers.setFieldValue("raisonSociale", val)}
                                                  placeholder={"Raison sociale"}
                                                  isInvalid={!!helpers.errors.raisonSociale}
                                             />
                                             <div className={"mb-2"}>{helpers.errors.raisonSociale && <div className={"text-danger"}>{helpers.errors.raisonSociale}</div>}</div>
                                        </div>
                                   </div>

                                   {/* SIRET */}
                                   <div className="row mb-5">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">SIRET</label>

                                        <div className="col-lg-8">
                                             <MyInptuMaskField
                                                  name={"siret"}
                                                  mask={"999 999 999 99999"}
                                                  value={helpers.values.siret}
                                                  placeholder={"SIRET"}
                                                  inputMode={"numeric"}
                                                  onChange={e => helpers.setFieldValue("siret", e.target.value)}
                                                  isInvalid={!!helpers.errors.siret}
                                             />

                                             <div className={"mb-2"}>{helpers.errors.siret && <div className={"text-danger"}>{helpers.errors.siret}</div>}</div>
                                        </div>
                                   </div>

                                   {/* Adresse */}
                                   <div className="row mb-5">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Adresse</label>

                                        <div className="col-lg-8">
                                             <MyInputField
                                                  name={"adresse"}
                                                  value={helpers.values.adresse}
                                                  onChange={val => helpers.setFieldValue("adresse", val)}
                                                  placeholder={"Adresse"}
                                                  isInvalid={!!helpers.errors.adresse}
                                             />
                                             <div className={"mb-2"}>{helpers.errors.adresse && <div className={"text-danger"}>{helpers.errors.adresse}</div>}</div>
                                        </div>
                                   </div>

                                   {/* Ville */}
                                   <div className="row mb-5">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Ville</label>

                                        <div className="col-lg-8">
                                             <MyInputField
                                                  name={"ville"}
                                                  value={helpers.values.ville}
                                                  onChange={val => helpers.setFieldValue("ville", val)}
                                                  placeholder={"Ville"}
                                                  isInvalid={!!helpers.errors.ville}
                                             />
                                             <div className={"mb-2"}>{helpers.errors.ville && <div className={"text-danger"}>{helpers.errors.ville}</div>}</div>
                                        </div>
                                   </div>

                                   {/* Code postal */}
                                   <div className="row mb-5">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Code postal</label>

                                        <div className="col-lg-8">
                                             <MyInputField
                                                  name={"codePostal"}
                                                  value={helpers.values.codePostal}
                                                  onChange={val => helpers.setFieldValue("codePostal", val)}
                                                  placeholder={"Code postal"}
                                                  isInvalid={!!helpers.errors.codePostal}
                                             />
                                             <div className={"mb-2"}>{helpers.errors.codePostal && <div className={"text-danger"}>{helpers.errors.codePostal}</div>}</div>
                                        </div>
                                   </div>

                                   {/* Pays */}
                                   <div className="row mb-5">
                                        <label className="col-lg-4 col-form-label fw-bold fs-6 required">Pays</label>

                                        <div className="col-lg-8">
                                             <MySelectGetAllDataFromServer
                                                  name={"pays"}
                                                  value={helpers.values.pays}
                                                  url={API_GET_PAYS_URL}
                                                  onChange={val => helpers.setFieldValue("pays", val)}
                                                  defaultOptions={
                                                       currentUser?.fournisseur?.fournisseur?.pays
                                                            ? [
                                                                   {
                                                                        value: currentUser?.fournisseur?.fournisseur?.pays.id as number,
                                                                        label: currentUser?.fournisseur?.fournisseur?.pays.nomFr as string,
                                                                   },
                                                              ]
                                                            : undefined
                                                  }
                                                  placeholder={"Pays"}
                                             />
                                             <div className={"mb-2"}>{helpers.errors.pays && <div className={"text-danger"}>{helpers.errors.pays}</div>}</div>
                                        </div>
                                   </div>

                                   <div className="separator bg-primary my-5" />

                                   {/* Submit button */}
                                   <div className={"d-flex justify-content-end gap-5"}>
                                        {toggleToPreviewMode && (
                                             <button
                                                  className={"btn btn-sm btn-outline btn-active-secondary d-inline-flex align-items-center"}
                                                  type={"button"}
                                                  onClick={toggleToPreviewMode}
                                                  disabled={helpers.isSubmitting}
                                             >
                                                  Annuler
                                             </button>
                                        )}
                                        <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? <>Appliquer les modifications</> : <MySimpleSpinner size={"sm"} />}
                                        </button>
                                   </div>
                              </Form>
                         )
                    }}
               </Formik>
          </>
     )
}

interface IFournisseurEditInfoByFournisseurModel {
     raisonSociale?: string | null
     siret?: string | null
     adresse?: string | null
     ville?: string | null
     codePostal?: string | null
     pays?: number | null
     logo?: File | null
}

export default CompteInfoFournisseurEditionMode
